import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Phone, 
  Mail, 
  Calendar,
  RefreshCw,
  Loader2,
  FileText,
  CreditCard,
  Package,
  UserCheck,
  DollarSign,
  Building2,
  Home,
  X,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';

const SupplierList = () => {
  const { user } = useAuth();
  const { formatCurrency, currencySymbol } = useSettings();
  const navigate = useNavigate();

  // State management
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supplierStats, setSupplierStats] = useState({
    total_suppliers: 0,
    active_suppliers: 0,
    wholesale_suppliers: 0,
    total_balance: 0
  });

  // UI State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSupplierForDelete, setSelectedSupplierForDelete] = useState(null);
  const [deleteSupplierLoading, setDeleteSupplierLoading] = useState(false);

  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    type: 'all',
    sort_by: 'name'
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 15,
    totalPages: 1,
    totalItems: 0
  });

  // Basic handlers

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getSuppliers({
        ...filters,
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage
      });
      
      if (response.success) {
        setSuppliers(response.data.suppliers || response.data || []);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.last_page || response.last_page || 1,
          totalItems: response.data.total || response.total || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      if (error.message.includes('401') || error.message.includes('Authentication')) {
        setError('Please log in to view suppliers');
        toast.error('Authentication required. Please log in.');
      } else {
        setError('Failed to load suppliers from database');
        toast.error('Failed to load suppliers. Please check your connection.');
      }
      // Clear suppliers on error
      setSuppliers([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalItems: 0
      }));
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  const fetchSupplierStats = useCallback(async () => {
    try {
      const response = await apiService.getSupplierStats();
      if (response.success) {
        setSupplierStats({
          total_suppliers: response.data.total || 0,
          active_suppliers: response.data.active || 0,
          wholesale_suppliers: response.data.wholesale || 0,
          total_balance: 0 // This would need to be calculated from individual supplier balances
        });
      }
    } catch (error) {
      console.error('Error fetching supplier stats:', error);
      // Calculate stats from the suppliers data we already have
      const totalSuppliers = suppliers.length;
      const activeSuppliers = suppliers.filter(s => s.status === 'active').length;
      const wholesaleSuppliers = suppliers.filter(s => s.is_wholesale).length;
      const totalBalance = suppliers.reduce((sum, s) => sum + (parseFloat(s.balance) || 0), 0);
      
      setSupplierStats({
        total_suppliers: totalSuppliers,
        active_suppliers: activeSuppliers,
        wholesale_suppliers: wholesaleSuppliers,
        total_balance: totalBalance
      });
    }
  }, [suppliers]);

  useEffect(() => {
    // Always try to fetch suppliers from the database
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Update stats when suppliers data changes
  useEffect(() => {
    if (suppliers.length > 0) {
      fetchSupplierStats();
    }
  }, [suppliers, fetchSupplierStats]);

  // Refetch data when pagination changes
  useEffect(() => {
    fetchSuppliers();
  }, [pagination.currentPage, pagination.itemsPerPage, fetchSuppliers]);



  // Debounced search state
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Update filters when debounced search changes
  useEffect(() => {
    if (searchInput !== debouncedSearch) {
      setIsSearching(true);
    }
    setFilters(prev => ({ ...prev, search: debouncedSearch }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearch, searchInput]);

  // Filter handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  // Form state
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    contact_person: '',
    status: 'active',
    is_wholesale: false,
    balance: 0,
    credit_limit: 0,
    payment_terms: null,
    tax_number: '',
    notes: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Supplier action handlers
  const handleAddSupplier = () => {
    setIsEditMode(false);
    setSelectedSupplier(null);
    setSupplierForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      contact_person: '',
      status: 'active',
      is_wholesale: false,
      balance: 0,
      credit_limit: 0,
      payment_terms: null,
      tax_number: '',
      notes: ''
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleViewSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedSupplier(null);
  };

  const handleEditSupplier = (supplier) => {
    setIsEditMode(true);
    setSelectedSupplier(supplier);
    setSupplierForm({
      name: supplier.name || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      contact_person: supplier.contact_person || '',
      status: supplier.status || 'active',
      is_wholesale: supplier.is_wholesale || false,
      balance: supplier.balance || 0,
      credit_limit: supplier.credit_limit || 0,
      payment_terms: supplier.payment_terms || null,
      tax_number: supplier.tax_number || '',
      notes: supplier.notes || ''
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleDeleteSupplier = (supplier) => {
    setSelectedSupplierForDelete(supplier);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedSupplierForDelete) return;

    setDeleteSupplierLoading(true);
    try {
      const response = await apiService.deleteSupplier(selectedSupplierForDelete.id);
      
      if (response.success) {
        toast.success('Supplier deleted successfully!', {
          duration: 3000,
          icon: '✅',
          style: {
            background: '#10B981',
            color: '#fff',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '12px',
          },
        });
        setShowDeleteModal(false);
        setSelectedSupplierForDelete(null);
        fetchSuppliers(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to delete supplier');
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error('Failed to delete supplier: ' + error.message, {
        duration: 3000,
      });
    } finally {
      setDeleteSupplierLoading(false);
    }
  };

  // Form handlers
  const handleFormChange = (field, value) => {
    setSupplierForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!supplierForm.name.trim()) {
      errors.name = 'Supplier name is required';
    }
    
    if (!supplierForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(supplierForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!supplierForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (supplierForm.balance < 0) {
      errors.balance = 'Balance cannot be negative';
    }
    
    if (supplierForm.credit_limit < 0) {
      errors.credit_limit = 'Credit limit cannot be negative';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      // Prepare form data with proper types
      const formData = {
        ...supplierForm,
        payment_terms: supplierForm.payment_terms === '' || supplierForm.payment_terms === null 
          ? null 
          : parseInt(supplierForm.payment_terms, 10),
        credit_limit: parseFloat(supplierForm.credit_limit) || 0,
        balance: parseFloat(supplierForm.balance) || 0
      };
      
      if (isEditMode) {
        // Update existing supplier
        const response = await apiService.updateSupplier(selectedSupplier.id, formData);
        if (response.success) {
          toast.success('Supplier updated successfully!', {
            duration: 3000,
            icon: '✅',
            style: {
              background: '#10B981',
              color: '#fff',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '12px',
            },
          });
          setShowAddModal(false);
          fetchSuppliers(); // Refresh the list
        }
      } else {
        // Create new supplier
        const response = await apiService.createSupplier(formData);
        if (response.success) {
          toast.success('Supplier created successfully!', {
            duration: 3000,
            icon: '✅',
            style: {
              background: '#10B981',
              color: '#fff',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '12px',
            },
          });
          setShowAddModal(false);
          fetchSuppliers(); // Refresh the list
        }
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error('Failed to save supplier. Please try again.', {
        duration: 3000,
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setFormErrors({});
    setSelectedSupplier(null);
    setIsEditMode(false);
  };

  // Status and type badges
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', text: 'Active' },
      inactive: { color: 'bg-red-100 text-red-800', text: 'Inactive' },
      suspended: { color: 'bg-yellow-100 text-yellow-800', text: 'Suspended' }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getTypeBadge = (isWholesale) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isWholesale 
          ? 'bg-blue-100 text-blue-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {isWholesale ? 'Wholesale' : 'Retail'}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-8">
              {/* Page Header */}
              <div className="bg-white rounded-2xl mt-20 shadow-xl border border-gray-200 p-8 mb-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Building2 className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                        Supplier Management
                      </h1>
                      <p className="text-gray-600 text-lg">Manage your suppliers and their information with ease</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => {
                        setError(null);
                        fetchSuppliers();
                      }}
                      disabled={loading}
                      className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                    >
                      <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                    <button 
                      onClick={() => navigate('/supplier-payments')}
                      className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl"
                    >
                      <CreditCard size={20} />
                      Payments
                    </button>
                    <button 
                      onClick={handleAddSupplier}
                      className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Plus size={20} />
                      Add Supplier
                    </button>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-white rounded-2xl shadow-xl border border-red-200 p-6 mb-8">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                        <svg className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-bold text-red-800">Error</h3>
                      <div className="mt-1 text-red-700">
                        <p>{error}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Beautiful Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600 mb-1">Total Suppliers</p>
                      <p className="text-3xl font-bold text-blue-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : supplierStats.total_suppliers || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600 mb-1">Active Suppliers</p>
                      <p className="text-3xl font-bold text-green-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : supplierStats.active_suppliers || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                      <UserCheck className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600 mb-1">Wholesale</p>
                      <p className="text-3xl font-bold text-orange-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : supplierStats.wholesale_suppliers || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Package className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-600 mb-1">Total Balance</p>
                      <p className="text-3xl font-bold text-red-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : formatCurrency(supplierStats.total_balance || 0)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters and Search */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Filter className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Filters & Search</h2>
                    <p className="text-gray-600">Find and filter your suppliers</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
                  <div className="relative">
                    {isSearching ? (
                      <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 animate-spin" size={18} />
                    ) : (
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    )}
                    <input 
                      type="text" 
                      placeholder="Search suppliers..." 
                      value={searchInput}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                    />
                  </div>
                  <select 
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <select 
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value="all">All Types</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="retail">Retail</option>
                  </select>
                  <select 
                    value={filters.sort_by}
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value="name">Sort by Name</option>
                    <option value="email">Sort by Email</option>
                    <option value="created_at">Sort by Date</option>
                    <option value="balance">Sort by Balance</option>
                  </select>
                  <select 
                    value={pagination.itemsPerPage}
                    onChange={(e) => {
                      setPagination(prev => ({ 
                        ...prev, 
                        itemsPerPage: parseInt(e.target.value),
                        currentPage: 1 
                      }));
                    }}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value={10}>10 per page</option>
                    <option value={15}>15 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2">
                    <Filter size={16} />
                    Apply Filters
                  </button>
                </div>
              </div>

              {/* Suppliers Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Supplier
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Balance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center">
                            <div className="flex items-center justify-center">
                              <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
                              <span className="ml-2 text-gray-600">Loading suppliers...</span>
                            </div>
                          </td>
                        </tr>
                      ) : suppliers.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center">
                            <div className="text-center">
                              <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">No suppliers found</h3>
                              <p className="mt-1 text-sm text-gray-500">
                                {filters.search || filters.status !== 'all' || filters.type !== 'all' 
                                  ? 'Try adjusting your search or filters.' 
                                  : 'Get started by adding your first supplier.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        suppliers.map((supplier) => (
                          <tr key={supplier.id} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                    <span className="text-sm font-medium text-orange-600">
                                      {supplier.name?.charAt(0).toUpperCase() || 'S'}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {supplier.name || 'Unknown Supplier'}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {supplier.supplierID || supplier.id}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{supplier.email || 'No email'}</div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Phone className="h-3 w-3 mr-1" />
                                {supplier.phone || 'No phone'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getTypeBadge(supplier.is_wholesale)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(supplier.status || 'active')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(supplier.balance || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleViewSupplier(supplier)}
                                  className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="View"
                                >
                                  <Eye size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    View
                                  </span>
                                </button>
                                <button
                                  onClick={() => handleEditSupplier(supplier)}
                                  className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="Edit"
                                >
                                  <Edit size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Edit
                                  </span>
                                </button>
                                <button
                                  onClick={() => handleDeleteSupplier(supplier)}
                                  className="group/btn relative p-2 text-red-600 bg-red-100 hover:bg-red-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="Delete"
                                >
                                  <Trash2 size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Delete
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Beautiful Pagination */}
              {suppliers.length > 0 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow-sm">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}</span> of{' '}
                        <span className="font-medium">{pagination.totalItems}</span> suppliers
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                          disabled={pagination.currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => setPagination(prev => ({ ...prev, currentPage: page }))}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pagination.currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                          disabled={pagination.currentPage === pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}

              {/* Add/Edit Supplier Modal */}
              {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
                    <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            {isEditMode ? <Edit size={20} className="text-white" /> : <Plus size={20} className="text-white" />}
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">{isEditMode ? 'Edit Supplier' : 'Add New Supplier'}</h2>
                            <p className="text-white text-opacity-80 text-sm">
                              {isEditMode ? 'Update supplier information' : 'Create a new supplier in your system'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={handleCloseModal}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          Close
                        </button>
                      </div>
                    </div>

                    <form onSubmit={handleFormSubmit} className="overflow-y-auto max-h-[calc(95vh-100px)]">
                      <div className="p-6 space-y-6">
                        {/* Basic Information Section */}
                        <div className="bg-gray-50 rounded-xl p-4">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                            Basic Information
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Supplier Name *
                              </label>
                              <input
                                type="text"
                                value={supplierForm.name}
                                onChange={(e) => handleFormChange('name', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  formErrors.name ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Enter supplier name"
                              />
                              {formErrors.name && (
                                <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Address *
                              </label>
                              <input
                                type="email"
                                value={supplierForm.email}
                                onChange={(e) => handleFormChange('email', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  formErrors.email ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="supplier@example.com"
                              />
                              {formErrors.email && (
                                <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phone Number *
                              </label>
                              <input
                                type="tel"
                                value={supplierForm.phone}
                                onChange={(e) => handleFormChange('phone', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  formErrors.phone ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="+233 24 123 4567"
                              />
                              {formErrors.phone && (
                                <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contact Person
                              </label>
                              <input
                                type="text"
                                value={supplierForm.contact_person}
                                onChange={(e) => handleFormChange('contact_person', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Contact person name"
                              />
                            </div>

                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Address
                              </label>
                              <textarea
                                value={supplierForm.address}
                                onChange={(e) => handleFormChange('address', e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter supplier address"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Business Information Section */}
                        <div className="bg-gray-50 rounded-xl p-4">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Package className="h-5 w-5 mr-2 text-green-600" />
                            Business Information
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                              </label>
                              <select
                                value={supplierForm.status}
                                onChange={(e) => handleFormChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="suspended">Suspended</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Business Type
                              </label>
                              <select
                                value={supplierForm.is_wholesale ? 'wholesale' : 'retail'}
                                onChange={(e) => handleFormChange('is_wholesale', e.target.value === 'wholesale')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="retail">Retail</option>
                                <option value="wholesale">Wholesale</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Tax Number
                              </label>
                              <input
                                type="text"
                                value={supplierForm.tax_number}
                                onChange={(e) => handleFormChange('tax_number', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Tax identification number"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Payment Terms (Days)
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={supplierForm.payment_terms || ''}
                                onChange={(e) => handleFormChange('payment_terms', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., 30, 45, 60"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Financial Information Section */}
                        <div className="bg-gray-50 rounded-xl p-4">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <DollarSign className="h-5 w-5 mr-2 text-yellow-600" />
                            Financial Information
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Current Balance ({currencySymbol})
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={supplierForm.balance}
                                onChange={(e) => handleFormChange('balance', parseFloat(e.target.value) || 0)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  formErrors.balance ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="0.00"
                              />
                              {formErrors.balance && (
                                <p className="mt-1 text-sm text-red-600">{formErrors.balance}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Credit Limit ({currencySymbol})
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={supplierForm.credit_limit}
                                onChange={(e) => handleFormChange('credit_limit', parseFloat(e.target.value) || 0)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  formErrors.credit_limit ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="0.00"
                              />
                              {formErrors.credit_limit && (
                                <p className="mt-1 text-sm text-red-600">{formErrors.credit_limit}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Notes Section */}
                        <div className="bg-gray-50 rounded-xl p-4">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <FileText className="h-5 w-5 mr-2 text-purple-600" />
                            Additional Notes
                          </h3>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Notes
                            </label>
                            <textarea
                              value={supplierForm.notes}
                              onChange={(e) => handleFormChange('notes', e.target.value)}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Any additional notes about this supplier..."
                            />
                          </div>
                        </div>

                        {/* Form Actions */}
                        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={handleCloseModal}
                            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={formLoading}
                            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {formLoading ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {isEditMode ? 'Updating...' : 'Creating...'}
                              </>
                            ) : (
                              <>
                                {isEditMode ? <Edit className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                                {isEditMode ? 'Update Supplier' : 'Create Supplier'}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* View Supplier Modal */}
              {showViewModal && selectedSupplier && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-200">
                    {/* Header */}
                    <div className="relative overflow-hidden bg-gradient-to-r from-blue-500 via-purple-600 to-blue-600 px-6 py-4 text-white">
                      {/* Background Pattern */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
                      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
                      
                      <div className="relative z-10 flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg border border-white/30">
                            <Building2 size={24} className="text-white" />
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold">Supplier Details</h2>
                            <p className="text-white/90 text-sm font-medium">{selectedSupplier.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              handleCloseViewModal();
                              handleEditSupplier(selectedSupplier);
                            }}
                            className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-sm font-semibold rounded-xl transition-all duration-200 border border-white/30 shadow-lg hover:shadow-xl flex items-center gap-2"
                          >
                            <Edit size={16} />
                            Edit
                          </button>
                          <button
                            onClick={handleCloseViewModal}
                            className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 border border-white/30 shadow-lg hover:shadow-xl"
                          >
                            <X size={20} className="text-white" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Basic Information */}
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 shadow-sm">
                          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <Building2 size={20} className="text-blue-600" />
                            Basic Information
                          </h3>
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Supplier Name</label>
                              <p className="text-lg font-bold text-gray-900 mt-1">{selectedSupplier.name}</p>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Contact Person</label>
                              <p className="text-gray-900 font-medium mt-1">{selectedSupplier.contact_person || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                              <div className="mt-1">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                  selectedSupplier.status === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {selectedSupplier.status === 'active' ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Type</label>
                              <div className="mt-1">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                  selectedSupplier.is_wholesale 
                                    ? 'bg-purple-100 text-purple-800' 
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {selectedSupplier.is_wholesale ? 'Wholesale' : 'Retail'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Contact Information */}
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 shadow-sm">
                          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <Phone size={20} className="text-green-600" />
                            Contact Information
                          </h3>
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Email Address</label>
                              <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                                <Mail size={16} className="text-gray-400" />
                                {selectedSupplier.email || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Phone Number</label>
                              <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                                <Phone size={16} className="text-gray-400" />
                                {selectedSupplier.phone || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Address</label>
                              <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                                <Home size={16} className="text-gray-400" />
                                {selectedSupplier.address || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Tax Number</label>
                              <p className="text-gray-900 font-medium mt-1">{selectedSupplier.tax_number || 'N/A'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Financial Information */}
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 shadow-sm">
                          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <DollarSign size={20} className="text-emerald-600" />
                            Financial Information
                          </h3>
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Current Balance</label>
                              <p className="text-2xl font-bold text-gray-900 mt-1">
                                {formatCurrency(selectedSupplier.balance || 0)}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Credit Limit</label>
                              <p className="text-lg font-bold text-gray-900 mt-1">
                                {formatCurrency(selectedSupplier.credit_limit || 0)}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Payment Terms</label>
                              <p className="text-gray-900 font-medium mt-1">{selectedSupplier.payment_terms || 'N/A'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Additional Information */}
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 shadow-sm">
                          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <FileText size={20} className="text-purple-600" />
                            Additional Information
                          </h3>
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Created Date</label>
                              <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                                <Calendar size={16} className="text-gray-400" />
                                {selectedSupplier.created_at ? new Date(selectedSupplier.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                }) : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Last Updated</label>
                              <p className="text-gray-900 font-medium mt-1 flex items-center gap-2">
                                <Calendar size={16} className="text-gray-400" />
                                {selectedSupplier.updated_at ? new Date(selectedSupplier.updated_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                }) : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Notes</label>
                              <p className="text-gray-900 font-medium mt-1 bg-white p-3 rounded-lg border border-gray-200">
                                {selectedSupplier.notes || 'No notes available'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedSupplierForDelete && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all">
                  {/* Header */}
                  <div className="bg-red-50 px-6 py-4 rounded-t-2xl border-b border-red-100">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-semibold text-gray-900">Confirm Delete</h2>
                      <button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setSelectedSupplierForDelete(null);
                        }}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                          <AlertCircle size={24} className="text-red-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Are you sure you want to delete this supplier?
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Name:</span>
                              <p className="text-gray-900">{selectedSupplierForDelete.name}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Email:</span>
                              <p className="text-gray-900">{selectedSupplierForDelete.email || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Phone:</span>
                              <p className="text-gray-900">{selectedSupplierForDelete.phone || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Status:</span>
                              <p className="text-gray-900 capitalize">{selectedSupplierForDelete.status || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          This will permanently delete the supplier and all associated data. This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="bg-gray-50 px-6 py-4 rounded-b-2xl">
                    <div className="flex items-center justify-end space-x-3">
                      <button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setSelectedSupplierForDelete(null);
                        }}
                        className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmDelete}
                        disabled={deleteSupplierLoading}
                        className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deleteSupplierLoading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 size={16} />
                            Delete Supplier
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
      </div>
    </Layout>
  );
};

export default SupplierList;
